/**
 * PATH: packages/api/src/workforce/client.ts
 * PURPOSE: THE fetch client for the workforce FastAPI service
 *          (/workforce-api/api/v1/…) — one implementation for every hub.
 * WHY: apps/workforce/src/lib/api.ts (full client) and
 *      apps/hr/src/lib/workforceApi.ts (a trimmed hand copy that had
 *      already drifted: no 401 refresh-retry, plain Error instead of a
 *      typed ApiError) were twin forks of the same client. Convergence
 *      C4-MR5 folds both onto this module; the app files become shims.
 *
 * FLOW:
 * ┌─────────┐  ┌──────────────────────┐  ┌───────────────────────────┐
 * │ caller  │─>│ buildUrl (+company_id│─>│ fetch → 401/403? silent   │
 * │         │  │ user_id) + bearer    │  │ master refresh, retry once│
 * └─────────┘  └──────────────────────┘  └───────────────────────────┘
 *
 * ERROR CONTRACT: non-2xx throws WorkforceApiError (extends Error). Its
 * `.message` is byte-identical to the historical `API error <status>: <body>`
 * string both forks produced, so every existing `catch (e) { e.message }`
 * caller keeps working, while `.status`/`.code`/`.detail` let callers branch
 * on the real failure (e.g. `no_linked_worker` vs a load error).
 *
 * DEPENDENCIES:
 * - @quotech/auth: getAuthCookie/getOrgCookie (cookies survive the Zustand
 *   rehydration race), useAuthStore, attemptMasterQuotechRefresh
 * - ./resolveBases: pure env → base-URL derivation
 * RELATED:
 * - apps/workforce/src/lib/api.ts, apps/hr/src/lib/workforceApi.ts: shims
 * - workforce-api/app/api/: the FastAPI service itself
 */

import {
  attemptMasterQuotechRefresh,
  getAuthCookie,
  getOrgCookie,
  useAuthStore,
} from '@quotech/auth'
import { resolveWorkforceBases, type WorkforceBases } from './resolveBases'

// ─── Base resolution (lazy singleton — avoids the Rollup TDZ bug where the
//     bundler reorders const declarations across inlined modules) ───────────

let _bases: WorkforceBases | undefined

function bases(): WorkforceBases {
  if (!_bases) {
    _bases = resolveWorkforceBases(
      (import.meta.env?.VITE_API_URL as string) || '',
      (import.meta.env?.VITE_BACKEND_ORIGIN as string) || '',
    )
  }
  return _bases
}

/** Base URL of the workforce FastAPI service (…/workforce-api/api/v1). */
export function getWorkforceApiBase(): string {
  return bases().workforceApiBase
}

/** Origin of the main Django backend (scheme + host, no path). */
export function getBackendOrigin(): string {
  return bases().backendOrigin
}

// ─── Error shape ─────────────────────────────────────────────────────────────

/**
 * A non-2xx response from workforce-api. Subclasses Error so existing
 * `instanceof Error` / `.message` callers keep working.
 */
export class WorkforceApiError extends Error {
  readonly status: number
  readonly body: string
  readonly code?: string
  readonly detail?: string

  constructor(status: number, body: string) {
    super(`API error ${status}: ${body}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    const parsed = safeParseFastapiDetail(body)
    this.code = parsed.code
    this.detail = parsed.message
  }
}

/**
 * Pure: a workforce-api error body -> its `detail.code` + human message.
 * FastAPI shapes errors as `{detail: "…"}` OR `{detail: {code, message}}`.
 * Tolerant: non-JSON / unexpected shapes yield empty fields.
 */
export function safeParseFastapiDetail(body: string): { code?: string; message?: string } {
  try {
    const parsed = JSON.parse(body) as { detail?: unknown }
    const detail = parsed?.detail
    if (typeof detail === 'string') return { message: detail }
    if (detail && typeof detail === 'object') {
      const d = detail as { code?: unknown; message?: unknown }
      return {
        code: typeof d.code === 'string' ? d.code : undefined,
        message: typeof d.message === 'string' ? d.message : undefined,
      }
    }
  } catch {
    // Non-JSON body (e.g. an HTML 502): no structured detail to extract.
  }
  return {}
}

// ─── Client ──────────────────────────────────────────────────────────────────

export type WorkforceRequestOptions = RequestInit & {
  params?: Record<string, string>
  skipOrgId?: boolean
}

function authToken(): string | null {
  // PRIORITY: cookie > Zustand store (cookies survive rehydration races).
  return getAuthCookie() || useAuthStore.getState().accessToken
}

function jsonHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' }
  const token = authToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

function buildUrl(path: string, params?: Record<string, string>, skipOrgId?: boolean): string {
  const base = String(getWorkforceApiBase() || '').replace(/\/+$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  const url = new URL(`${base}${cleanPath}`, window.location.origin)

  if (!skipOrgId) {
    // Tenant context expected by the FastAPI auth dependency. Cookie-first
    // for the org id: the store is briefly empty during rehydration and a
    // missing company_id fails the backend org check.
    const { user, organisationId } = useAuthStore.getState()
    const orgId = getOrgCookie()?.id || organisationId || user?.organisation_id
    if (orgId) url.searchParams.append('company_id', String(orgId))
    if (user?.id) url.searchParams.append('user_id', String(user.id))
  }

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value)
    })
  }
  return url.toString()
}

/**
 * One-shot 401/403 recovery: a rejection from workforce-api usually means
 * the `quotech_auth` cookie was signed with a key the backend no longer
 * accepts. ONE silent master refresh re-mints the cookie with the current
 * signing key; headers are rebuilt (send() re-reads the cookie) and the
 * request retried exactly once. Best-effort: attemptMasterQuotechRefresh
 * never throws and returns false for v1-only/dead sessions, in which case
 * the original error surfaces.
 */
async function fetchWithAuthRetry(send: () => Promise<Response>): Promise<Response> {
  let response = await send()
  if (response.status === 401 || response.status === 403) {
    const refreshed = await attemptMasterQuotechRefresh()
    if (refreshed) response = await send()
  }
  return response
}

async function request<T>(
  method: string,
  path: string,
  options?: WorkforceRequestOptions,
  data?: unknown,
): Promise<T> {
  const url = buildUrl(path, options?.params, options?.skipOrgId)
  const send = (): Promise<Response> =>
    fetch(url, {
      method,
      headers: jsonHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    })

  const response = await fetchWithAuthRetry(send)
  if (!response.ok) {
    throw new WorkforceApiError(response.status, await response.text())
  }
  return response.json()
}

export const workforceApiClient = {
  get<T>(path: string, options?: WorkforceRequestOptions): Promise<T> {
    return request<T>('GET', path, options)
  },
  post<T>(path: string, data?: unknown, options?: WorkforceRequestOptions): Promise<T> {
    return request<T>('POST', path, options, data)
  },
  put<T>(path: string, data?: unknown, options?: WorkforceRequestOptions): Promise<T> {
    return request<T>('PUT', path, options, data)
  },
  patch<T>(path: string, data?: unknown, options?: WorkforceRequestOptions): Promise<T> {
    return request<T>('PATCH', path, options, data)
  },
  delete<T>(path: string, options?: WorkforceRequestOptions): Promise<T> {
    return request<T>('DELETE', path, options)
  },

  /**
   * Multipart upload (e.g. a receipt photo). Sends FormData WITHOUT a JSON
   * Content-Type so the browser sets the multipart boundary itself; reuses
   * the same auth + one-shot 401/403 refresh-and-retry + error contract as
   * request(). Kept separate so the JSON path is untouched.
   */
  async upload<T>(
    path: string,
    formData: FormData,
    options?: WorkforceRequestOptions,
  ): Promise<T> {
    const url = buildUrl(path, options?.params, options?.skipOrgId)
    const uploadHeaders = (): HeadersInit => {
      const headers: HeadersInit = {}
      const token = authToken()
      if (token) headers['Authorization'] = `Bearer ${token}`
      return headers
    }
    const send = (): Promise<Response> =>
      fetch(url, { method: 'POST', headers: uploadHeaders(), body: formData })

    const response = await fetchWithAuthRetry(send)
    if (!response.ok) {
      throw new WorkforceApiError(response.status, await response.text())
    }
    return response.json()
  },

  /**
   * Binary GET (week timesheet xlsx). Same bearer, company_id, and one-shot
   * 401/403 retry as request(). Does not JSON-parse the body.
   */
  async getBlob(path: string, options?: WorkforceRequestOptions): Promise<Blob> {
    const url = buildUrl(path, options?.params, options?.skipOrgId)
    const headers = (): HeadersInit => {
      const next: HeadersInit = {}
      const token = authToken()
      if (token) next['Authorization'] = `Bearer ${token}`
      return next
    }
    const send = (): Promise<Response> =>
      fetch(url, { method: 'GET', headers: headers() })
    const response = await fetchWithAuthRetry(send)
    if (!response.ok) {
      throw new WorkforceApiError(response.status, await response.text())
    }
    return response.blob()
  },
}
